import { HexColorPicker } from "react-colorful";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export type OverlayConfig = {
  enabled: boolean;
  color: string;
  opacity: number; // 0..1
  blur: number; // 0..40
};

type Props = {
  value: OverlayConfig;
  onChange: (next: OverlayConfig) => void;
};

export const OverlayPanel = ({ value, onChange }: Props) => {
  return (
    <div className="min-w-0 space-y-4 rounded-md border border-border p-3">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Label className="text-xs">Dim & blur overlay</Label>
          <p className="text-[11px] text-muted-foreground">Improves text legibility on busy backgrounds.</p>
        </div>
        <Switch checked={value.enabled} onCheckedChange={(enabled) => onChange({ ...value, enabled })} />
      </div>

      {value.enabled && (
        <div className="space-y-4">
          <div className="flex min-w-0 items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="h-9 w-9 shrink-0 rounded border border-border"
                  style={{ background: value.color }}
                  aria-label="Overlay color"
                />
              </PopoverTrigger>
              <PopoverContent className="w-56 space-y-2">
                <HexColorPicker color={value.color} onChange={(color) => onChange({ ...value, color })} style={{ width: "100%" }} />
                <Input value={value.color} onChange={(e) => onChange({ ...value, color: e.target.value })} />
              </PopoverContent>
            </Popover>
            <span className="min-w-0 text-xs text-muted-foreground">Overlay color</span>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Opacity</Label>
              <span className="text-xs tabular-nums text-muted-foreground">{Math.round(value.opacity * 100)}%</span>
            </div>
            <Slider
              value={[Math.round(value.opacity * 100)]}
              min={0}
              max={100}
              step={1}
              onValueChange={(v) => onChange({ ...value, opacity: v[0] / 100 })}
              className="mt-2"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Blur</Label>
              <span className="text-xs tabular-nums text-muted-foreground">{value.blur}px</span>
            </div>
            <Slider
              value={[value.blur]}
              min={0}
              max={40}
              step={1}
              onValueChange={(v) => onChange({ ...value, blur: v[0] })}
              className="mt-2"
            />
          </div>
        </div>
      )}
    </div>
  );
};
